import {
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { RightArrowCtaIcon } from "assets/images/onboarding/icons";
import lightBulbLottie from "assets/images/onboarding/light-bulb.json";
import { CheckIcon, ClockIcon, ExternalLinkIcon } from "components";
import Lottie from "lottie-react";
import {
  addMixpanelEvent,
  type AnalyticsEventKeys,
} from "utilz/helpers/mixpanelLogger";
import { DRAWER_STEPS } from "./drawerSteps";
import { STEP_ICON_MAP } from "./stepIconMap";
import type { DrawerStep, DrawerStepKey } from "./types";

const PRIMARY_CTA_EVENTS: Record<DrawerStepKey, AnalyticsEventKeys> = {
  welcome: "onb_bookademo_clicked",
  connect_support_mail: "onb_connect_support_mail_clicked",
  team: "onb_add_teammates_clicked",
  branding: "onb_branding_clicked",
  customers: "onb_create_customer_clicked",
  channels: "onb_download_apps_clicked",
  integrations: "onb_connect_rmm_clicked",
};

const SECONDARY_CTA_EVENTS: Record<DrawerStepKey, AnalyticsEventKeys> = {
  welcome: "onb_chatsupport_clicked",
  connect_support_mail: "onb_connect_support_mail_doc_clicked",
  team: "onb_add_teammates_doc_clicked",
  branding: "onb_branding_doc_clicked",
  customers: "onb_create_customer_doc_clicked",
  channels: "onb_download_apps_doc_clicked",
  integrations: "onb_connect_rmm_doc_clicked",
};

const PROTIP_CTA_EVENTS: Partial<Record<DrawerStepKey, AnalyticsEventKeys>> = {
  connect_support_mail: "onb_workflows_clicked",
  team: "onb_roles_and_permissions_clicked",
};

interface OnboardingDrawerContentProps {
  activeStepKey: DrawerStepKey;
  isCompleted: boolean;
  isLastStep: boolean;
  isActiveStepRevisit: boolean;
  onMarkComplete: (step: DrawerStep) => void;
  onSkip: (step: DrawerStep) => void;
  isMarkingComplete: boolean;
}

const numberedCircleSx = {
  width: "16px",
  height: "16px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
} as const;

const diamondSx = {
  color: "#5EBB70",
  flexShrink: 0,
  fontSize: "14px",
  lineHeight: 1,
  mt: "4px",
} as const;

export const OnboardingDrawerContent = ({
  activeStepKey,
  isCompleted,
  isLastStep,
  isActiveStepRevisit,
  onMarkComplete,
  onSkip,
  isMarkingComplete,
}: OnboardingDrawerContentProps) => {
  const theme = useTheme();
  const step = DRAWER_STEPS.find((s) => s.key === activeStepKey);

  if (!step) return null;

  const { content } = step;
  const StepIcon = STEP_ICON_MAP[content.icon];

  const handleCtaClick = (
    route: string | (() => void),
    event?: AnalyticsEventKeys,
  ) => {
    if (event) {
      addMixpanelEvent({ event });
    }
    if (typeof route === "string") {
      window.open(route, "_blank", "noopener,noreferrer");
    } else {
      route();
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        height: "100%",
        padding: "48px 32px 32px 32px",
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        overflowY: "auto",
        backgroundColor: theme.palette.primitive.neutrals.neutral[900],
        position: "relative",
      }}
    >
      {/* Header Section */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        gap="16px"
      >
        <Stack gap="8px" maxWidth="720px" flex={1}>
          {StepIcon && (
            <StepIcon
              sx={{
                color: theme.palette.darkBg.high,
                width: "32px",
                height: "32px",
              }}
            />
          )}
          <Typography variant="h5" sx={{ color: theme.palette.darkBg.high }}>
            {content.title}
          </Typography>
          <Typography
            variant="body-small"
            sx={{
              color: theme.palette.darkBg.medium,
              lineHeight: 1.6,
            }}
          >
            {content.description}
          </Typography>

          {/* Time Badge */}
          {!content.hideTimeChip && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 12px",
                borderRadius: "6px",
                backgroundColor:
                  content.accentBgColor ?? "rgba(255, 255, 255, 0.05)",
                width: "fit-content",
              }}
            >
              <ClockIcon
                sx={{
                  color: content.accentColor ?? theme.palette.darkBg.low,
                  width: "14px",
                  height: "14px",
                }}
              />
              <Typography
                variant="caption-xs"
                sx={{ color: content.accentColor ?? theme.palette.darkBg.low }}
              >
                ~{step.estimatedTime}
              </Typography>
            </Box>
          )}
        </Stack>

        <SkipButton
          isLastStep={isLastStep}
          isRevisit={isActiveStepRevisit}
          isCompleted={isCompleted}
          onClick={() => onSkip(step)}
        />
      </Stack>

      {/* Divider */}
      <Box
        sx={{
          height: "1px",
          width: "100%",
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          flexShrink: 0,
        }}
      />

      {/* What you'll do - Numbered list */}
      {content.whatYoullDo && (
        <Stack gap="24px">
          <Typography
            variant="body-xsmall"
            sx={{
              color: theme.palette.darkBg.medium,
            }}
          >
            What you&apos;ll do
          </Typography>
          <Stack gap="24px">
            {content.whatYoullDo.map((item, index) => (
              <Stack
                key={index}
                direction="row"
                gap="12px"
                alignItems="flex-start"
              >
                <Box
                  sx={{
                    ...numberedCircleSx,
                    backgroundColor: content.accentColor,
                  }}
                >
                  <Typography variant="caption-xs">{index + 1}</Typography>
                </Box>
                <Typography
                  variant="body-small"
                  sx={{ color: theme.palette.darkBg.high, lineHeight: 1.6 }}
                >
                  {item.text}
                  {item.link && (
                    <>
                      {" "}
                      <Link
                        component="button"
                        onClick={() => handleCtaClick(item.link!.route)}
                        sx={{
                          color: theme.palette.secondary["purple-tint"],
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontSize: "inherit",
                          lineHeight: "inherit",
                          verticalAlign: "baseline",
                        }}
                      >
                        {item.link.label}
                      </Link>
                    </>
                  )}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}

      {/* Diamond bullets (Welcome step) */}
      {content.diamondBullets && (
        <Stack gap="24px">
          <Typography
            variant="body-xsmall"
            sx={{
              color: theme.palette.darkBg.medium,
            }}
          >
            What you&apos;ll do
          </Typography>
          <Stack gap="16px">
            {content.diamondBullets.map((bullet, index) => (
              <Stack
                key={index}
                direction="row"
                gap="12px"
                alignItems="flex-start"
              >
                <Box sx={diamondSx}>&#10022;</Box>
                <Typography
                  variant="body-small"
                  sx={{ color: theme.palette.darkBg.high, lineHeight: 1.6 }}
                >
                  {bullet.text}
                  {bullet.link && (
                    <>
                      {" "}
                      <Typography
                        component="a"
                        variant="body-small"
                        href={bullet.link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: theme.palette.primitive.brand.purple["500"],
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        {bullet.link.label}
                      </Typography>
                    </>
                  )}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}

      {/* Info Cards (Explore apps step) */}
      {content.infoCards && (
        <Stack gap="24px" maxWidth="386px">
          <Typography
            variant="body-xsmall"
            sx={{
              color: theme.palette.darkBg.medium,
            }}
          >
            What you&apos;ll do
          </Typography>
          <Stack gap="16px">
            {content.infoCards.map((card, index) => (
              <Box
                key={index}
                sx={{
                  padding: "24px",
                  borderRadius: "8px",
                  bgcolor: "#2C303A",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color:
                      card.title === "For end users" ? "#D2AD3B" : "#E9946B",
                    mb: "8px",
                  }}
                >
                  {card.title}
                </Typography>
                <Typography
                  variant="body-xsmall"
                  sx={{
                    color: theme.palette.darkBg.medium,
                    lineHeight: 1.6,
                  }}
                >
                  {card.body}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      )}

      {/* Action Buttons */}
      <Stack gap="24px" maxWidth="387px">
        <Stack direction="row" alignItems="center" gap="8px" width={"100%"}>
          <Button
            variant="contained"
            onClick={() =>
              handleCtaClick(
                content.primaryCta.route,
                PRIMARY_CTA_EVENTS[activeStepKey],
              )
            }
            sx={{
              ...theme.typography["button-label"],
              lineHeight: "16px",
              backgroundColor: theme.palette.secondary["purple-tint"],
              color: theme.palette.primitive.neutrals.neutral[900],
              py: "8px",
              borderRadius: "6px",
              minWidth: "160px",
              textTransform: "none",
              gap: "8px",
              "&:hover": {
                backgroundColor: theme.palette.secondary["purple-tint-2"],
              },
              width: "50%",
            }}
          >
            {content.primaryCta.label}
            <RightArrowCtaIcon style={{ width: "16px", height: "16px" }} />
          </Button>
          {content.secondaryCta && (
            <Button
              variant="outlined"
              onClick={() =>
                handleCtaClick(
                  content.secondaryCta?.route || "",
                  SECONDARY_CTA_EVENTS[activeStepKey],
                )
              }
              endIcon={
                <ExternalLinkIcon sx={{ width: "16px", height: "16px" }} />
              }
              sx={{
                ...theme.typography["button-label"],
                lineHeight: "16px",
                color: theme.palette.darkBg.high,
                borderColor: theme.palette.primitive.neutrals.neutral[700],
                py: "8px",
                borderRadius: "6px",
                textTransform: "none",
                gap: "8px",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  borderColor: theme.palette.primitive.neutrals.neutral[700],
                },
                width: "50%",
              }}
            >
              {content.secondaryCta.label}
            </Button>
          )}
        </Stack>
        <Button
          variant="outlined"
          fullWidth
          disabled={
            content.markCompleteVariant === "completed" ||
            isCompleted ||
            isMarkingComplete
          }
          onClick={() => onMarkComplete(step)}
          endIcon={
            isMarkingComplete ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : (
              <CheckIcon sx={{ width: "16px", height: "16px" }} />
            )
          }
          sx={{
            ...theme.typography["button-label"],
            lineHeight: "16px",
            color:
              content.markCompleteVariant === "completed" || isCompleted
                ? theme.palette.primitive.system.green[500]
                : theme.palette.darkBg.high,
            borderColor:
              content.markCompleteVariant === "completed" || isCompleted
                ? theme.palette.primitive.system.green[500]
                : theme.palette.primitive.neutrals.neutral[700],
            py: "8px",
            borderRadius: "6px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              borderColor: theme.palette.primitive.neutrals.neutral[700],
            },
            "&.Mui-disabled": {
              color: theme.palette.primitive.system.green[500],
              borderColor: theme.palette.primitive.system.green[500],
              backgroundColor: "#1e3c23",
            },
          }}
        >
          {content.markCompleteVariant === "completed" || isCompleted
            ? "Completed"
            : "Mark as complete"}
        </Button>
      </Stack>

      {/* Pro Tip Card */}
      {content.proTip && (
        <Stack
          direction="row"
          alignItems="flex-start"
          sx={{
            padding: "16px",
            borderRadius: "8px",
            backgroundColor: "#2C303A",
            maxWidth: "387px",
          }}
        >
          <Box
            sx={{
              width: "40px",
              height: "40px",
            }}
          >
            <Lottie animationData={lightBulbLottie} loop={true} />
          </Box>

          <Stack gap="16px" sx={{ ml: "8px" }}>
            <Typography
              variant="subtitle1"
              sx={{ color: theme.palette.darkBg.high }}
            >
              {content.proTip.title}
            </Typography>

            <Typography
              variant="body-xsmall"
              sx={{
                color: theme.palette.darkBg.high,
                lineHeight: 1.6,
              }}
            >
              {content.proTip.body}
            </Typography>

            {content.proTip.bullets && (
              <Stack
                component="ul"
                sx={{ margin: 0, paddingLeft: "20px" }}
                gap="4px"
              >
                {content.proTip.bullets.map((bullet, index) => (
                  <Typography
                    key={index}
                    component="li"
                    variant="body-xsmall"
                    sx={{
                      color: theme.palette.darkBg.high,
                      lineHeight: 1.6,
                    }}
                  >
                    {bullet}
                  </Typography>
                ))}
              </Stack>
            )}

            {content.proTip.cta && (
              <Button
                variant="outlined"
                onClick={() =>
                  handleCtaClick(
                    content.proTip!.cta!.route,
                    PROTIP_CTA_EVENTS[activeStepKey],
                  )
                }
                endIcon={
                  <ExternalLinkIcon sx={{ width: "16px", height: "16px" }} />
                }
                sx={{
                  ...theme.typography["button-label"],
                  lineHeight: "16px",
                  color: theme.palette.darkBg.high,
                  borderColor: theme.palette.darkBg.high,
                  py: "8px",
                  borderRadius: "6px",
                  textTransform: "none",
                  width: "fit-content",
                  gap: "8px",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderColor: theme.palette.darkBg.high,
                  },
                }}
              >
                {content.proTip.cta.label}
              </Button>
            )}
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

const SkipButton = ({
  isLastStep,
  isRevisit,
  isCompleted,
  onClick,
}: {
  isLastStep: boolean;
  isRevisit: boolean;
  isCompleted: boolean;
  onClick: () => void;
}) => {
  const theme = useTheme();
  // "Skip" implies skipping work; if the step is already done, the verb is
  // "Next" instead. Also "Next" when the user has navigated away and back.
  const showAsNext = isRevisit || isCompleted;
  // On the last step, "Next" has nothing to navigate to — hide in that case.
  if (isLastStep && showAsNext) return null;
  return (
    <Button
      variant="outlined"
      onClick={onClick}
      sx={{
        ...theme.typography["button-label"],
        lineHeight: "16px",
        color: theme.palette.darkBg.high,
        borderColor: theme.palette.primitive.neutrals.neutral[700],
        py: "8px",
        px: "16px",
        borderRadius: "6px",
        textTransform: "none",
        minWidth: 0,
        flexShrink: 0,
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.05)",
          borderColor: theme.palette.primitive.neutrals.neutral[700],
        },
      }}
    >
      {showAsNext ? "Next" : "Skip"}
    </Button>
  );
};
